import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AUTH_ENTRY_ROUTES = ['/auth/login', '/auth/register'];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthEntryRoute = AUTH_ENTRY_ROUTES.some(route => error.config?.url?.includes(route));
    const companySuspended = error.response?.status === 403
      && error.response?.data?.message === 'Company access is suspended';
    if (!isAuthEntryRoute && (error.response?.status === 401 || companySuspended)) {
      localStorage.removeItem('token');
      localStorage.removeItem('auth-storage');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (name: string, email: string, password: string, organizationName?: string) =>
    api.post('/auth/register', { name, email, password, organizationName }),
  me: () => api.get('/auth/me'),
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.put('/users/me/change-password', { currentPassword, newPassword }),
};

export const projectsApi = {
  getAll: (workspaceId?: string) => api.get('/projects', { params: { workspaceId } }),
  getAllForSelector: (workspaceId?: string) => api.get('/projects/selector', { params: { workspaceId } }),
  getById: (id: string) => api.get(`/projects/${id}`),
  create: (data: unknown) => api.post('/projects', data),
  update: (id: string, data: unknown) => api.put(`/projects/${id}`, data),
  delete: (id: string) => api.delete(`/projects/${id}`),
  getMembers: (id: string) => api.get(`/projects/${id}/members`),
  addMember: (id: string, userId: string, role: string) =>
    api.post(`/projects/${id}/members`, { userId, role }),
  removeMember: (id: string, userId: string) =>
    api.delete(`/projects/${id}/members/${userId}`),
};

export const workspacesApi = {
  getAll: () => api.get('/workspaces'),
  create: (data: { name: string; color?: string }) => api.post('/workspaces', data),
};

export const tasksApi = {
  getAll: (params?: { status?: string; assigneeId?: string; workspaceId?: string }) => api.get('/tasks', { params }),
  getByProject: (projectId: string) => api.get(`/tasks/project/${projectId}`),
  getById: (id: string) => api.get(`/tasks/${id}`),
  create: (data: unknown) => api.post('/tasks', data),
  update: (id: string, data: unknown) => api.put(`/tasks/${id}`, data),
  delete: (id: string) => api.delete(`/tasks/${id}`),
  updatePosition: (id: string, status: string, position: number) =>
    api.patch(`/tasks/${id}/position`, { status, position }),
  getComments: (id: string) => api.get(`/tasks/${id}/comments`),
  addComment: (id: string, content: string) =>
    api.post(`/tasks/${id}/comments`, { content }),
  getSubtasks: (id: string) => api.get(`/tasks/${id}/subtasks`),
  getAttachments: (id: string) => api.get(`/tasks/${id}/attachments`),
  uploadAttachment: (id: string, file: File) => {
    const data = new FormData();
    data.append('file', file);
    return api.post(`/tasks/${id}/attachments`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const personalTasksApi = {
  getAll: () => api.get('/personal-tasks'),
  create: (data: { title: string; description?: string; due_date?: string; recurrence?: string }) =>
    api.post('/personal-tasks', data),
  update: (id: string, data: { title?: string; description?: string; due_date?: string; recurrence?: string }) =>
    api.put(`/personal-tasks/${id}`, data),
  complete: (id: string) => api.patch(`/personal-tasks/${id}/complete`),
  reopen: (id: string) => api.patch(`/personal-tasks/${id}/reopen`),
  delete: (id: string) => api.delete(`/personal-tasks/${id}`),
};

export const mileageApi = {
  getAll: (params?: { projectId?: string; vehicleId?: string; status?: string; startDate?: string; endDate?: string }) =>
    api.get('/mileage', { params }),
  getVehicles: () => api.get('/mileage/vehicles'),
  create: (data: unknown) => api.post('/mileage', data),
  update: (id: string, data: unknown) => api.put(`/mileage/${id}`, data),
  delete: (id: string) => api.delete(`/mileage/${id}`),
};

export const usersApi = {
  getAll: () => api.get('/users'),
  create: (data: unknown) => api.post('/users', data),
  update: (id: string, data: unknown) => api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
};

export const dashboardApi = {
  getStats: (workspaceId?: string) => api.get('/dashboard/stats', { params: { workspaceId } }),
};

export const integrationsApi = {
  getGoogleAuthUrl: () => api.get('/integrations/google/auth'),
  getGoogleStatus: () => api.get('/integrations/google/status'),
  disconnectGoogle: () => api.delete('/integrations/google/disconnect'),
  toggleGoogleSync: (autoSync: boolean) => api.patch('/integrations/google/toggle-sync', { autoSync }),
};


export const financeApi = {
  getSummary: (params?: { projectId?: string; startDate?: string; endDate?: string; regime?: string }) =>
    api.get('/finance/summary', { params }),
  getExpenses: (params?: { projectId?: string; supplierId?: string; status?: string; startDate?: string; endDate?: string; regime?: string }) =>
    api.get('/finance/expenses', { params }),
  createExpense: (data: unknown) => api.post('/finance/expenses', data),
  updateExpense: (id: string, data: unknown) => api.put(`/finance/expenses/${id}`, data),
  deleteExpense: (id: string) => api.delete(`/finance/expenses/${id}`),
  getExpenseAttachments: (id: string) => api.get(`/finance/expenses/${id}/attachments`),
  uploadExpenseAttachment: (id: string, file: File) => {
    const data = new FormData();
    data.append('file', file);
    return api.post(`/finance/expenses/${id}/attachments`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getSuppliers: () => api.get('/finance/suppliers'),
  getPaymentAccounts: () => api.get('/finance/payment-accounts'),
  getBudgetLines: (projectId?: string) => api.get('/finance/budget-lines', { params: { projectId } }),
  createBudgetLine: (data: unknown) => api.post('/finance/budget-lines', data),
  updateBudgetLine: (id: string, data: unknown) => api.put(`/finance/budget-lines/${id}`, data),
  deleteBudgetLine: (id: string) => api.delete(`/finance/budget-lines/${id}`),
};

export const attachmentsApi = {
  download: (id: string) => api.get(`/attachments/${id}/download`, { responseType: 'blob' }),
  delete: (id: string) => api.delete(`/attachments/${id}`),
};

export const notificationsApi = {
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (prefs: Record<string, boolean>) => api.put('/notifications/preferences', prefs),
};

export const adminApi = {
  getOrganizations: () => api.get('/admin/organizations'),
  getOrganization: (id: string) => api.get(`/admin/organizations/${id}`),
  createOrganization: (data: {
    organizationName: string;
    adminName: string;
    adminEmail: string;
    adminPassword: string;
  }) => api.post('/admin/organizations', data),
  updateOrganization: (
    id: string,
    data: { name?: string; status?: 'active' | 'inactive'; plan?: 'free' | 'pro' | 'enterprise' }
  ) => api.patch(`/admin/organizations/${id}`, data),
  replaceOrganizationOwner: (
    id: string,
    data: { adminName: string; adminEmail: string; adminPassword: string }
  ) => api.post(`/admin/organizations/${id}/owner`, data),
};

export default api;
